import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Loader2, Bell } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        updateUser({ is_subscribed: true });
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4" data-testid="payment-success-page">
      <Card className="max-w-md w-full rounded-sm border-0 shadow-lg">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-2">
                Verifying Payment
              </h1>
              <p className="text-gray-600">
                Please wait while we confirm your payment...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-2" data-testid="payment-success-title">
                Payment Successful!
              </h1>
              <p className="text-gray-600 mb-6">
                Thank you for subscribing to VisaAlerts. You'll now receive instant notifications when new visa appointments become available.
              </p>
              <Button 
                className="bg-[#0A0A0A] hover:bg-[#0A0A0A]/90 text-white font-semibold rounded-sm px-8"
                onClick={() => navigate('/dashboard')}
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {(status === 'timeout' || status === 'error' || status === 'expired') && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-yellow-100 rounded-full flex items-center justify-center">
                <Bell className="w-8 h-8 text-yellow-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] mb-2">
                {status === 'expired' ? 'Session Expired' : 'Verification Pending'}
              </h1>
              <p className="text-gray-600 mb-6">
                {status === 'expired' 
                  ? 'Your payment session has expired. Please try again.'
                  : 'We couldn\'t verify your payment immediately. Please check your email for confirmation or contact support.'}
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => navigate('/dashboard')}
                >
                  Go to Dashboard
                </Button>
                <Button 
                  className="bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-semibold rounded-sm"
                  onClick={() => navigate('/contact')}
                >
                  Contact Support
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
